import { ChartOptions } from 'chart.js';
import { Building2, Clock, Users, DollarSign, CreditCard } from 'lucide-react';
import { DropdownInterface } from '../../../../components/custom-table/custom-table.props';

export function DynamicStatusTag({ status }: { status: string }) {
  let bgColorClass = '';
  let textColorClass = '';
  switch (status) {
    case 'Pending':
      bgColorClass = 'bg-orange-100';
      textColorClass = 'text-orange-700';
      break;
    case 'inprogress':
      bgColorClass = 'bg-orange-100';
      textColorClass = 'text-orange-700';
      break;
    case 'Approved':
      bgColorClass = 'bg-green-100';
      textColorClass = 'text-green-700';
      break;
    case 'active':
      bgColorClass = 'bg-green-100';
      textColorClass = 'text-green-700';
      break;
    case 'Rejected':
      bgColorClass = 'bg-red-100';
      textColorClass = 'text-red-700';
      break;
    case 'Expired':
      bgColorClass = 'bg-red-800';
      textColorClass = 'text-white';
      break;
    case 'Active':
      bgColorClass = 'bg-green-100';
      textColorClass = 'text-green-700';
      break;
    case 'Near Expiry':
      bgColorClass = 'bg-yellow-100';
      textColorClass = 'text-yellow-700';
      break;
    default:
      bgColorClass = 'bg-gray-100';
      textColorClass = 'text-gray-700';
      break;
  }

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${bgColorClass} ${textColorClass} border-0`}
    >
      {status}
    </span>
  );
}

export const FrequencyOptionsTable = [
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 24hrs', value: '1d' },
];

export const StatusOptionsTable = [
  { label: 'Status', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

export const ExtendedButtonReason = [
  { id: 0, name: 'Client Request' },
  { id: 1, name: 'Onboarding Delay' },
  { id: 2, name: 'Special Case' },
  { id: 3, name: 'Other' },
];

export const dashboardConfig = {
  dashboardTitle: 'Company Registration Dashboard',
  statsConfig: [
    {
      title: 'Total Companies Registered',
      icon: Building2,
      value: 0,
      id: 'total_registered_account',
    },
    {
      title: 'Pending Approvals',
      icon: Clock,
      value: 0,
      id: 'total_pending_requests',
    },
    {
      title: 'Total Users Onboarded',
      icon: Users,
      value: 0,
      id: 'trial_user',
    },
    {
      title: 'Total Free Credit Distributed ($)',
      icon: DollarSign,
      value: 0,
      id: 'total_free_credits_distributed',
    },
    {
      title: 'Total Free Credit Used ($)',
      icon: CreditCard,
      value: 0,
      id: 'total_token_used',
    },
  ],
  chartConfig: [
    {
      title: 'Onboarded users per month',
      type: 'line',
      UserOnboardedChartData: {
        labels: [],
        datasets: [
          {
            label: 'Onboarded Users (%)',
            data: [], // Adjusted data points
            fill: false,
            borderColor: 'rgb(156 163 255)', // Tailwind indigo-300 (similar to the image)
            tension: 0.4, // Adjust tension for curve
            pointBackgroundColor: 'rgb(156 163 255)',
            pointRadius: 5,
          },
        ],
      },
    },
    {
      title: 'Registrations Over Time',
      type: 'bar',
      registrationsData: {
        labels: [],
        datasets: [
          {
            label: 'Registrations',
            data: [],
            backgroundColor: 'rgb(1, 115, 203)', // Exact blue from the image (approximated)
          },
        ],
      },
    },
    {
      title: 'Credit Allocation by Company (Top 10)',
      type: 'bar',
      creditAllocationData: {
        labels: [],
        datasets: [
          {
            label: 'Free Credit',
            data: [],
            backgroundColor: 'rgb(59 130 246)', // Tailwind blue-500
            barThickness: 12,
          },
          {
            label: 'Used Credit',
            data: [],
            backgroundColor: 'rgb(245 158 11)', // Tailwind amber-500
            barThickness: 12,
          },
        ],
      },
    },
  ],
  tableConfig: {
    title: 'Pending Registration Queue',
    table_headings: [
      'Company Name',
      'PRIMARY USER EMAIL',
      'DATE OF REQUEST',
      'STATUS',
      'BUDGET EXPIRY',
      'ACTIONS',
    ],
    table_data: [],
  },
  reConsiderButtonText: 'Reconsider',
  extendExpiryButtonText: 'Extend Expiry',
  extendExpiryConfig: {
    title: 'Extend Budget Expiry',
    modalDesc: 'Set a new budget expiry date',
    saveData: 'Extend Expiry',
    inputFields: [
      {
        label: 'Company Name',
        type: 'text',
        name: 'company_name',
      },
      {
        label: 'Current Expiry Date',
        type: 'text',
        placeholder: 'dd/mm/yyyy',
        name: 'current_expiry_date',
      },
      {
        label: 'New Expiry Date',
        type: 'date',
        placeholder: 'dd/mm/yyyy',
        name: 'expiry_date',
      },
      {
        label: 'Reason for Extension',
        type: 'select',
        placeholder: 'Select a reason',
        options: ExtendedButtonReason,
        name: 'extension_reason',
      },
      {
        label: 'Additional Notes (max 250 characters)',
        placeholder: 'Description',
        name: 'additional_notes',
      },
    ],
  },
  companyDetailsConfig: {
    title: 'Company Details',
    modalDesc: 'View the budget details of your company',
    inputFields: [
      {
        label: 'Company Name',
        type: 'text',
        name: 'company_name',
      },
      {
        label: 'Company Website',
        type: 'text',
        name: 'company_website',
      },
      {
        label: 'Work Email ID',
        type: 'text',
        name: 'work_email_id',
      },
      {
        label: 'Primary Admin',
        type: 'text',
        name: 'primary_admin',
      },
      {
        label: 'Free Credits',
        type: 'number',
        name: 'free_credits',
      },
      {
        label: 'Credits Used',
        type: 'number',
        name: 'credits_used',
      },
      {
        label: 'Budget Expiry',
        type: 'text',
        name: 'budget_expiry',
      },
      {
        label: 'Onboarded Users',
        type: 'text',
        name: 'onboarded_users',
      },
    ],
    buttonText: 'Close',
  },
  reConsiderConfig: {
    saveData: 'Reconsider',
    content: 'Are you sure you want to reconsider?',
  },
};

export const dateFilterOptions: DropdownInterface[] = [
  {
    options: [
      { value: 'today', label: 'Today' },
      { value: '7days', label: 'Last 7 Days' },
      { value: '90days', label: 'Last 90 Days' },
    ],
    heading: 'Date Range',
  },
];

export const statusFilterOptions: DropdownInterface[] = [
  {
    options: [
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'pending', label: 'Pending' },
    ],
    heading: 'Status',
  },
];

export const registrationsOptions: ChartOptions<'bar'> = {
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        stepSize: 10,
        callback(value: string | number) {
          // Corrected type
          return value;
        },
        color: 'rgb(108, 115, 125)', // Approximate gray from the image
      },
      grid: {
        color: 'rgb(238, 242, 246)', // Approximate light gray grid
      },
    },
    x: {
      ticks: {
        color: 'rgb(108, 115, 125)', // Approximate gray from the image
      },
      grid: {
        display: false,
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
      text: 'Registrations Over Time',
      align: 'start',
      color: 'rgb(31, 41, 55)', // Tailwind gray-800 equivalent
      font: {
        size: 16,
      },
    },
    tooltip: {
      backgroundColor: 'white',
      titleColor: 'rgb(31, 41, 55)',
      bodyColor: 'rgb(55, 65, 81)',
      borderColor: 'rgb(209, 213, 219)',
      borderWidth: 1,
    },
  },
} as const;

export const userOnboardChartOptions: ChartOptions<'line'> = {
  scales: {
    y: {
      beginAtZero: false,
      min: 0,
      ticks: {
        stepSize: 20, // Ensure y-axis ticks match the image
        callback(value: string | number) {
          return value;
        },
        color: '#6b7280', // Tailwind gray-500
      },
      grid: {
        color: 'rgba(209, 213, 219, 0.5)', // Light gray grid lines (Tailwind gray-300 with opacity)
        display: true,
      },
    },
    x: {
      ticks: {
        color: '#6b7280', // Tailwind gray-500
      },
    },
  },
  plugins: {
    legend: {
      display: false, // Legend is not visible in the image
    },
    title: {
      display: false,
      text: 'Onboarded users per month',
      color: '#374151', // Tailwind gray-700
      font: {
        size: 16,
      },
      align: 'start', // Align title to the left
    },
    tooltip: {
      callbacks: {
        label(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += context.parsed.y;
          }
          return label;
        },
        labelColor() {
          return {
            borderColor: 'rgb(156 163 255)',
            backgroundColor: 'rgb(156 163 255)',
          };
        },
      },
      backgroundColor: 'white',
      titleColor: '#1f2937',
      bodyColor: '#374151',
      borderColor: '#d1d5db',
      borderWidth: 1,
    },
  },
} as const;

export const creditAllocationOptions: ChartOptions<'bar'> = {
  scales: {
    y: {
      beginAtZero: true,
      min: 0,
      title: {
        display: false,
        text: 'Amount ($)',
        color: '#6b7280', // Tailwind gray-500
      },
      ticks: {
        stepSize: 250,
        color: '#6b7280', // Tailwind gray-500
        callback(value: string | number) {
          // Corrected type
          return `$${value}`;
        },
      },
    },
    x: {
      beginAtZero: true,
      ticks: {
        color: '#6b7280', // Tailwind gray-500
      },
      grid: {
        display: false, // No vertical grid lines in the image
      },
    },
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        color: '#374151', // Tailwind gray-700
      },
    },
    title: {
      display: false,
      text: 'Credit Allocation by Company (Top 10)',
      align: 'start',
      color: '#374151', // Tailwind gray-700
      font: {
        size: 16,
      },
      padding: {
        top: 16,
        bottom: 16,
      },
    },
    tooltip: {
      backgroundColor: 'white',
      titleColor: '#1f2937',
      bodyColor: '#374151',
      borderColor: '#d1d5db',
      borderWidth: 1,
      callbacks: {
        label(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += `$${context.parsed.y.toFixed(0)}`;
          }
          return label;
        },
      },
    },
  },
} as const;
